import { MetricCard } from './metric-card';
import { metricDefs } from '../metric-defs';
import { Skeleton } from '@/components/ui/skeleton';
import { useFunnel, useMetricsPerKey } from '@/hooks/use-analytics-v2';
import type { CoverageFunnelResult, CoverageFunnelStage, ScalarMetricKey, ScalarMetricResult } from '@/types/analyticsV2';
import type { MetricDef } from '../metric-defs';

interface CoverageFunnelSectionProps {
  fromDate: string;
  toDate: string;
  /** Account these metrics are scoped to. */
  accountId: number;
  /** Brand to scope to, or null for the whole account. */
  buId?: string | null;
}

/** Ordered top-to-bottom, descending from the CS Tickets baseline. */
const FUNNEL_STAGES: { key: keyof Omit<CoverageFunnelResult, 'csTickets' | 'hitl'>; label: string }[] = [
  { key: 'aiGenerated', label: 'AI Generated Response' },
  { key: 'surfaced', label: 'Surfaced (confidence ≥ 0.5)' },
  { key: 'nonSurfaced', label: 'Non-Surfaced' },
  { key: 'hitlPending', label: 'HITL Pending' },
  { key: 'agentAdded', label: 'Agent Added to Composer' },
  { key: 'agentSent', label: 'Agent Sent to Customer' },
];

/** Accuracy / Coverage split cards reuse the overview card mapping. */
const ACCURACY_KEYS: ScalarMetricKey[] = ['aiAccuracy', 'aiSurfacedAccuracy'];
const COVERAGE_KEYS: ScalarMetricKey[] = ['aiGeneratedCoverage', 'aiSurfacedCoverage'];
const SPLIT_KEYS: ScalarMetricKey[] = [...ACCURACY_KEYS, ...COVERAGE_KEYS];

const cardValue = (
  def: MetricDef,
  result: ScalarMetricResult | undefined,
): { value: number | null; denominator?: number; unit?: string } => {
  if (!result) return { value: null };
  switch (def.cardDisplay) {
    case 'count-with-denominator':
      return { value: result.current.count, denominator: result.current.total };
    case 'percent':
      return { value: result.current.percent, unit: '%' };
  }
};

const cardTrend = (def: MetricDef, result: ScalarMetricResult | undefined): number | null => {
  if (!result) return null;
  return result.trend[def.trendField];
};

const formatPct = (pct: number | null): string => (pct === null ? '—' : `${pct}%`);

/** Clamp a stage's share to a 0–100 bar width; null pct renders an empty bar. */
const barWidth = (pct: number | null): number => (pct === null ? 0 : Math.max(0, Math.min(100, pct)));

function FunnelBar({ label, stage, baseline }: { label: string; stage: CoverageFunnelStage; baseline: boolean }) {
  return (
    <div className='flex flex-col gap-1'>
      <div className='flex items-baseline justify-between gap-3 text-[12px] leading-[16px]'>
        <span className='font-medium text-[#2b2e34]'>{label}</span>
        <span className='text-[#5f656f] tabular-nums'>
          {stage.count.toLocaleString()}
          <span className='ml-2 text-[#2b2e34]'>{formatPct(stage.pct)}</span>
        </span>
      </div>
      <div className='h-2.5 w-full overflow-hidden rounded-full bg-gray-100'>
        <div
          className={baseline ? 'h-full rounded-full bg-[#7C3AED]' : 'h-full rounded-full bg-[#a78bfa]'}
          style={{ width: `${barWidth(stage.pct)}%` }}
          role='img'
          aria-label={`${label}: ${stage.count.toLocaleString()}, ${formatPct(stage.pct)} of CS tickets`}
        />
      </div>
    </div>
  );
}

function HitlStat({ label, value }: { label: string; value: number }) {
  return (
    <div className='flex flex-col gap-1 rounded-3xl border border-gray-500 bg-white p-4 text-left'>
      <span className='text-[13px] leading-[18px] font-medium text-[#2b2e34]'>{label}</span>
      <span className='text-[20px] leading-[28px] font-semibold text-[#2b2e34] tabular-nums'>
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function FunnelSkeleton() {
  return (
    <div className='flex flex-col gap-2.5 rounded-3xl border border-gray-300 bg-white p-4'>
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className='flex flex-col gap-1'>
          <Skeleton className='h-4 w-40 rounded-md' />
          <Skeleton className='h-2.5 w-full rounded-full' />
        </div>
      ))}
    </div>
  );
}

/**
 * Coverage Funnel tab for the super-admin Internal Analytics page. Renders the
 * shadow report's Coverage Funnel as horizontal bars (CS Tickets is the 100%
 * baseline, every stage shows its share of it), the HITL approved/rejected/
 * pending split, and the Generated vs Surfaced accuracy/coverage cards.
 */
export function CoverageFunnelSection({ fromDate, toDate, accountId, buId }: CoverageFunnelSectionProps) {
  const { data, isLoading, isError } = useFunnel(fromDate, toDate, accountId, buId);
  const splitResults = useMetricsPerKey(SPLIT_KEYS, fromDate, toDate, accountId, buId);
  const splitByKey = Object.fromEntries(splitResults.map((r) => [r.key, r]));

  const isEmpty = !isLoading && !isError && (!data || data.csTickets === 0);

  const renderSplitCard = (key: ScalarMetricKey) => {
    const def = metricDefs[key];
    const r = splitByKey[key];
    const { value, denominator, unit } = cardValue(def, r?.data);
    return (
      <MetricCard
        key={key}
        title={def.label}
        description={def.description}
        value={value}
        denominator={denominator}
        unit={unit}
        trend={cardTrend(def, r?.data)}
        isLoading={r?.isLoading ?? false}
      />
    );
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-2'>
        <h2 className='text-[12px] text-gray-800 font-medium'>Coverage Funnel</h2>
        {isLoading ? (
          <FunnelSkeleton />
        ) : isError ? (
          <div className='flex items-center justify-center h-40 rounded-3xl border border-gray-300 bg-white text-sm text-muted-foreground'>
            Failed to load the coverage funnel.
          </div>
        ) : isEmpty || !data ? (
          <div className='flex items-center justify-center h-40 rounded-3xl border border-gray-300 bg-white text-sm text-muted-foreground'>
            No CS tickets in this range.
          </div>
        ) : (
          <div className='flex flex-col gap-2.5 rounded-3xl border border-gray-300 bg-white p-4'>
            <FunnelBar
              label='CS Tickets'
              stage={{ count: data.csTickets, pct: 100 }}
              baseline
            />
            {FUNNEL_STAGES.map(({ key, label }) => (
              <FunnelBar key={key} label={label} stage={data[key]} baseline={false} />
            ))}
          </div>
        )}
      </div>

      <div className='flex flex-col gap-2'>
        <h2 className='text-[12px] text-gray-800 font-medium'>HITL breakdown</h2>
        <div className='grid grid-cols-3 gap-3'>
          <HitlStat label='Approved' value={data?.hitl.approved ?? 0} />
          <HitlStat label='Rejected' value={data?.hitl.rejected ?? 0} />
          <HitlStat label='Pending' value={data?.hitl.pending ?? 0} />
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <h2 className='text-[12px] text-gray-800 font-medium'>Accuracy</h2>
        <div className='grid grid-cols-2 gap-3'>{ACCURACY_KEYS.map(renderSplitCard)}</div>
      </div>

      <div className='flex flex-col gap-2'>
        <h2 className='text-[12px] text-gray-800 font-medium'>Coverage</h2>
        <div className='grid grid-cols-2 gap-3'>{COVERAGE_KEYS.map(renderSplitCard)}</div>
      </div>
    </div>
  );
}
